import React, { useState } from 'react';
import { Payment } from '../types';
import { useLanguage } from '../i18n/LanguageProvider';
import {
  Listbox, ListboxButton, ListboxOptions, ListboxOption,
} from '@headlessui/react';
import { resolveCartContent } from '../util';

interface PaymentsInfoProps {
  payments: Payment[] | undefined;
  onPaymentClick: (payment: Payment) => void;
  classNumber?: string;
  serialFormNumber?: number; 
}

const PaymentsInfo: React.FC<PaymentsInfoProps> = ({ payments, onPaymentClick, classNumber, serialFormNumber }) => {
  const formatAmount = (amount: number): string => {
    if (!amount) return '0';
    return new Intl.NumberFormat('sr-RS').format(amount);
  };
  const { t } = useLanguage();
  const [selectedAmounts, setSelectedAmounts] = useState<Record<number, number>>({});

  if (!payments || payments.length === 0) {
    return (
      <div className="pp-empty">
        <div className="pp-empty-emoji">📋</div>
        <div>{t('noPaymentsDisplayed')}</div>
      </div>
    );
  }

  return (
    <div className="pp-payments-wrap">
      <div className="pp-panel-header">
        <span>{t('paymentInfo')}</span>
      </div>
      <div className="pp-panel-header bg-gray-50">
        <span className="font-normal text-sm italic">
          {
            t(payments.length === 1 ? 'paymentDescriptionFines' : 'paymentDescriptionFinesAndFees')
          }
          {
            classNumber
              ? `${t('classNumber')} ${classNumber}`
              : `${t('serialNumber')} ${serialFormNumber ?? ''}`
          }
        </span>
      </div>

      <div className="pp-panel-body">
        {payments.map((payment: Payment, index: number) => {
          const isPaymentCompleted = payment.paymentStatus === 1;
          const isTransactionSuccessful = payment.transactionStatus === true;

          const amountOptions =
            payment.amounts && payment.amounts.length > 0
              ? payment.amounts
              : (() => {
                  const opts: number[] = [payment.amount];
                  if (typeof payment.discountAmount === 'number' && payment.discountAmount !== payment.amount) {
                    opts.push(payment.discountAmount);
                  }
                  return opts;
                })();

          const effectiveAmount = selectedAmounts[index] ?? amountOptions[0];

          return (
            <div
              key={index}
              className={`pp-payment-card ${isPaymentCompleted ? 'pp-payment-card--done' : ''}`}
            >
              <div className="pp-card-row">
                <div className="min-w-0 flex-1 leading-snug">
                  <div className="pp-card-title">
                    {resolveCartContent(payment.cartContent, t) || t('paymentInfo')}
                  </div>
                  {isTransactionSuccessful && (
                    <div className="pp-transaction-ok">
                      ✅ {t('transactionDone')}
                    </div>
                  )}
                </div>
                <div className="text-right">
                  {amountOptions.length > 1 ? (
                    <div className="relative inline-block">
                      <Listbox
                        value={effectiveAmount}
                        onChange={(val: number) =>
                          setSelectedAmounts((prev) => ({ ...prev, [index]: Number(val) }))
                        }
                      >
                        <ListboxButton className="pp-lb-btn">
                          {`${formatAmount(effectiveAmount)} EUR`} ▾
                        </ListboxButton>
                        
                        {isPaymentCompleted ? null :
                        <ListboxOptions className="pp-lb-panel">
                          {amountOptions.map((amt) => (
                            <ListboxOption key={amt} value={amt}>
                              {({ active, selected }: { active: boolean; selected: boolean }) => (
                                <div
                                  className={`pp-lb-item ${active ? 'pp-lb-item--active' : ''} ${selected ? 'pp-lb-item--selected' : ''}`}
                                >
                                  {formatAmount(amt)} EUR
                                </div>
                              )}
                            </ListboxOption>
                          ))}
                        </ListboxOptions>}
                      
                      </Listbox>
                    </div>
                  ) : (
                    <div className="pp-amount-static">{`${formatAmount(amountOptions[0])} EUR`}</div>
                  )}
                </div>
              </div>

              {!isPaymentCompleted && (
                <div className="mt-2 flex justify-end">
                  <button
                    className="pp-btn-primary"
                    onClick={() => onPaymentClick({ ...payment, amount: effectiveAmount })}
                  >
                    {t('buttonPay')}
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PaymentsInfo;